import React from "react"
import SettingsCard from "../../../components/common/SettingsCard"
import { TextField, Toggle, SelectField, MenuItem } from "material-ui"
import strings from "../../../localization/strings"
import AccountStore from "../../../stores/AccountStore"
import PayConfiguration from "../../../components/pay_configuration/PayConfiguration"
import AccrualAdjustmentRules from "../../../components/accural_adjustment_rules/AccrualAdjustmentRules"
import autobind from "class-autobind"
import { patchAccount } from "../../../actions/AccountActions"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import { IAccountSettings } from "../../../models"
import * as utils from "../../../helpers/utils"
import ExceptionSettings from "./ExceptionSettings"
import CustomReport from "./custom_report/CustomReport"
import AgreementSet from "../../../components/agreement/settting/AgreementSet"
import IntegrationCard from "./integration/IntegrationCard"
import PayrollExportScheduledJob from "./scheduled_job/PayrollExportScheduledJob"
import ScheduledJobsCard from "./scheduled_job/ScheduledJobsCard"
import AppStateStore from "../../../stores/AppStateStore"
import ImportCard from "./import/ImportCard"
import TimeApprovals from "./time_approvals/TimeApprovals"
import ClientSettings from "../../../components/clients/client_settings/ClientSettings"
import CustomFieldSettings from "../../../components/custom_field_settings/CustomFieldSettings"
import AccountFeatureSettings from "../../../components/account/settings/AccountFeatureSettings"
import AccountMobileFeatureSettings from "../../../components/account/settings/AccountMobileFeatureSettings"
import _ from "lodash"
import NoteTypesCard from "./NoteTypes/NoteTypesCard"
import RecalcTimecards from "./recalc_timecards/RecalcTimecards"

interface IProps {
	accountId: string
}
interface IState {
	expanded: string
	generalExpanded: boolean
	settings: IAccountSettings
	biometricThreshold: string
	defaultFaceSpaceSetCount: string
	defaultGeofencePerimeter: string
	defaultMicroLocationPerimeter: string
	accountEnableLocationException: boolean
	accountEnableLocationExceptionOnTransfer: boolean
	dateFormat: string
	currencyFormat: string
	languageId: string
	defaultCountryCode: string
	employeeAssignedOnly: boolean
	userAssignedOnly: boolean
	errorMsg: any
	currencyList: string[]
	requireAgreement: boolean
}
const styles = {
	errorTextStyle: {
		position: "absolute",
		bottom: -10
	},
	textStyle: {
		marginBottom: 10,
		width: 280
	}
}

export default class AccountSettings extends React.Component<IProps, IState> {
	constructor(props) {
		super(props)
		this.state = {
			expanded: "",
			generalExpanded: false,
			settings: null,
			biometricThreshold: "",
			defaultFaceSpaceSetCount: "",
			defaultGeofencePerimeter: "",
			defaultMicroLocationPerimeter: "",
			accountEnableLocationException: null,
			accountEnableLocationExceptionOnTransfer: false,
			dateFormat: "",
			currencyFormat: "",
			languageId: "",
			defaultCountryCode: "",
			employeeAssignedOnly: null,
			userAssignedOnly: null,
			errorMsg: {},
			currencyList: ["USD", "AUD", "BRL", "EUR", "GBP", "CAD", "CNY", "INR", "MXN"],
			requireAgreement: false
		}
		autobind(this)
	}

	componentDidMount() {
		AccountStore.on("account_change", this.setupState)
		this.setupState()

		const settings = AccountStore.getCurrentAccountSettings()
		const localizationSettings = settings && settings.LocalizationSettings
		const dateFormat = localizationSettings.DateTimeFormat ? localizationSettings.DateTimeFormat : ""
		const currencyFormat = localizationSettings.CurrencyFormat ? localizationSettings.CurrencyFormat : ""
		const languageId = localizationSettings.LanguageId ? localizationSettings.LanguageId : ""
		const defaultCountryCode = localizationSettings.DefaultCountryCode ? localizationSettings.DefaultCountryCode : ""
		const hierarchySettings = settings && settings.HierarchySettings
		const userAssignedOnly = hierarchySettings
			? hierarchySettings.DisableHierarchyItemsForAccountUserUnlessAssigned
				? hierarchySettings.DisableHierarchyItemsForAccountUserUnlessAssigned
				: false
			: false
		const employeeAssignedOnly = hierarchySettings
			? hierarchySettings.DisableHierarchyItemsForEmployeeUnlessAssigned
				? hierarchySettings.DisableHierarchyItemsForEmployeeUnlessAssigned
				: false
			: false
		const requireAgreement = settings.RequireAgreement
		this.setState({
			dateFormat,
			currencyFormat,
			languageId,
			defaultCountryCode,
			employeeAssignedOnly,
			userAssignedOnly,
			requireAgreement
		})
	}

	componentWillUnmount() {
		AccountStore.removeListener("account_change", this.setupState)
	}

	onExpandClick(type) {
		this.setState({
			expanded: this.state.expanded === type ? "" : type
		})
	}

	setupState() {
		const settings = AccountStore.getCurrentAccountSettings()
		const biomSettings = settings && settings.BiometricSettings
		const biometricThreshold = biomSettings ? String(biomSettings.DefaultThreshold) : ""
		const defaultFaceSpaceSetCount = biomSettings ? String(biomSettings.DefaultFaceSpaceSetCount) : ""
		const locationSettings = settings && settings.LocationSettings
		const defaultGeofencePerimeter = locationSettings ? String(locationSettings.DefaultGeoFencePerimeter) : ""
		const defaultMicroLocationPerimeter = locationSettings ? String(locationSettings.DefaultMicroLocationPerimeter) : ""
		const accountEnableLocationException = locationSettings ? !locationSettings.DisableLocationExceptions : true
		const accountEnableLocationExceptionOnTransfer = locationSettings ? locationSettings.EnableLocationExceptionOnTransfer : false
		this.setState({
			settings,
			biometricThreshold,
			defaultFaceSpaceSetCount,
			defaultGeofencePerimeter,
			defaultMicroLocationPerimeter,
			accountEnableLocationException,
			accountEnableLocationExceptionOnTransfer
		})
	}

	sendToApi(testParams, sendObject) {
		const result = this.formIsValid.apply(this, testParams)
		if (!result.isValid) {
			this.setupState()
			const errorMsg = result.value
			errorMsg.defaultFaceSpaceSetCount = ""
			this.setState({ errorMsg })
			return
		}
		patchAccount(this.props.accountId, sendObject)
	}

	handleChangeBiometricThreshold(e, value) {
		const errorMsg = strings.This_field_requires_a_number_between_0_and_100
		this.setState({ biometricThreshold: value })
		this.formIsValid("biometricThreshold", value, 0, 100, errorMsg)
	}

	handleOnBlurBiometricThreshold() {
		const errorMsg = "none"
		this.sendToApi(["biometricThreshold", Number(this.state.biometricThreshold), 0, 100, errorMsg], {
			AccountSettings: {
				BiometricSettings: {
					DefaultThreshold: this.state.biometricThreshold
				}
			}
		})
	}

	handleChangeFaceSpaceSetCount(e, value) {
		if (utils.validateInteger(value)) {
			const errorMsg = strings.This_field_requires_an_integer_between_2_and_10
			this.setState({ defaultFaceSpaceSetCount: value })
			this.formIsValid("defaultFaceSpaceSetCount", value, 2, 10, errorMsg)
		}
	}

	handleOnBlurFaceSpace() {
		const errorMsg = "none"
		this.sendToApi(["defaultFaceSpaceSetCount", Number(this.state.defaultFaceSpaceSetCount), 2, 10, errorMsg], {
			AccountSettings: {
				BiometricSettings: {
					DefaultFaceSpaceSetCount: this.state.defaultFaceSpaceSetCount
				}
			}
		})
	}

	handleChangeGeofencePerimeter(e, value) {
		const errorMsg = strings.This_field_requires_a_number_between_0_and_1000
		this.setState({ defaultGeofencePerimeter: value })
		this.formIsValid("defaultGeofencePerimeter", value, 0, 1000, errorMsg)
	}

	handleOnBlurGeofencePerimeter() {
		const errorMsg = "none"
		this.sendToApi(["defaultGeofencePerimeter", Number(this.state.defaultGeofencePerimeter), 0, 1000, errorMsg], {
			AccountSettings: {
				LocationSettings: {
					DefaultGeoFencePerimeter: this.state.defaultGeofencePerimeter
				}
			}
		})
	}

	handleChangeMicroLocationPerimeter(e, value) {
		const errorMsg = strings.This_field_requires_a_number_between_0_and_100
		this.setState({ defaultMicroLocationPerimeter: value })
		this.formIsValid("defaultMicroLocationPerimeter", value, 0, 100, errorMsg)
	}

	handleOnBlurMicroLocationPerimeter() {
		const errorMsg = "none"
		this.sendToApi(["defaultMicroLocationPerimeter", Number(this.state.defaultMicroLocationPerimeter), 0, 100, errorMsg], {
			AccountSettings: {
				LocationSettings: {
					DefaultMicroLocationPerimeter: this.state.defaultMicroLocationPerimeter
				}
			}
		})
	}

	handleToggleAccountLocationException(event, value) {
		this.setState({ accountEnableLocationException: value }, () => {
			patchAccount(this.props.accountId, { AccountSettings: { LocationSettings: { DisableLocationExceptions: !value } } })
		})
	}

	handleToggleAccountLocationExceptionOnTransfer(event, value) {
		this.setState({ accountEnableLocationExceptionOnTransfer: value }, () => {
			patchAccount(this.props.accountId, { AccountSettings: { LocationSettings: { EnableLocationExceptionOnTransfer: value } } })
		})
	}

	handleChangeDateFormat(e, value) {
		this.setState({ dateFormat: value })
	}

	handleOnBlurDateFormat() {
		patchAccount(this.props.accountId, { AccountSettings: { LocalizationSettings: { DateTimeFormat: this.state.dateFormat } } })
	}

	handleChangeCurrencyFormat(event, index, value) {
		this.setState({ currencyFormat: value }, () => {
			patchAccount(this.props.accountId, { AccountSettings: { LocalizationSettings: { CurrencyFormat: value } } })
		})
	}

	handleChangeLanguageId(event, value) {
		this.setState({ languageId: value })
	}

	handleChangeDefaultCountryCode(event, index, value) {
		this.setState({ defaultCountryCode: value }, () => {
			patchAccount(this.props.accountId, { AccountSettings: { LocalizationSettings: { DefaultCountryCode: value } } })
		})
	}

	handleOnBlurLanguageId() {
		patchAccount(this.props.accountId, { AccountSettings: { LocalizationSettings: { LanguageId: this.state.languageId } } })
	}

	handleToggleEmployeeAssignedOnly(event, value) {
		AppStateStore.setConfirmDialogContent({
			content: strings.Are_you_sure_you_want_to_change_this_account_setting,
			confirm: () =>
				this.setState({ employeeAssignedOnly: value }, () => {
					patchAccount(this.props.accountId, { AccountSettings: { HierarchySettings: { DisableHierarchyItemsForEmployeeUnlessAssigned: value } } })
				})
		})
	}

	handleToggleUserAssignedOnly(event, value) {
		AppStateStore.setConfirmDialogContent({
			content: strings.Are_you_sure_you_want_to_change_this_account_setting,
			confirm: () =>
				this.setState({ userAssignedOnly: value }, () => {
					patchAccount(this.props.accountId, { AccountSettings: { HierarchySettings: { DisableHierarchyItemsForAccountUserUnlessAssigned: value } } })
				})
		})
	}

	formIsValid(type, value, min, max, err = null) {
		let isValid = true
		let errorMsg = ""
		if (value === "") {
			errorMsg = err ? err : strings.This_is_a_required_field
			isValid = false
		} else if (value < min) {
			errorMsg = err ? err : strings.The_number_is_too_low
			isValid = false
		} else if (value > max) {
			errorMsg = err ? err : strings.The_number_is_too_high
			isValid = false
		}
		const tempErrMsg = {
			value: this.state.errorMsg,
			isValid
		}
		if (err === "none") {
			tempErrMsg.value[type] = null
		} else {
			tempErrMsg.value[type] = errorMsg
		}
		tempErrMsg.isValid = isValid
		return tempErrMsg
	}

	render() {
		const { settings } = this.state
		const locSettings = settings && settings.LocalizationSettings
		const isGlobalAdmin = permissionHelper.isGlobalAdmin()
		const timezoneNames = AccountStore.Timezones.map((_timeZone) => _timeZone.Name)
		return (
			<div className="AccountSettings">
				{settings && [
					<SettingsCard expanded={"general" === this.state.expanded} cardLabel={strings.General} handleExpandChange={() => this.onExpandClick("general")} key={1}>
						<SelectField
							data-intercom-target={"menu-AccountSettings-GeneralTab-Select-AccountDefaultTimezone"}
							floatingLabelText={strings.Account_Default_Timezone}
							style={{ width: 380 }}
							value={locSettings.DefaultIANATimezoneId}
							onChange={(e, value) =>
								AppStateStore.setConfirmDialogContent({
									content: strings.Are_you_sure_you_want_to_change_this_account_setting,
									confirm: () =>
										patchAccount(this.props.accountId, {
											AccountSettings: { LocalizationSettings: { DefaultIANATimezoneId: timezoneNames[value] } }
										})
								})
							}
						>
							{AccountStore.Timezones.map((_timezone, i) =>
								<MenuItem value={_timezone.Name} primaryText={_timezone.Label} key={i} />
							)}
						</SelectField>
					</SettingsCard>,
					<SettingsCard expanded={"biometric" === this.state.expanded} cardLabel={strings.Biometric} handleExpandChange={() => this.onExpandClick("biometric")} key={3}>
						<TextField
							data-intercom-target={"menu-AccountSettings-BiometricTab-Entry-BiometricThreshold"}
							floatingLabelText={strings.Biometric_Threshold}
							value={this.state.biometricThreshold}
							onChange={this.handleChangeBiometricThreshold}
							errorText={this.state.errorMsg.biometricThreshold}
							errorStyle={styles.errorTextStyle}
							style={styles.textStyle}
							onBlur={this.handleOnBlurBiometricThreshold}
							type="number"
						/>{" "}
						%<br />
						<TextField
							data-intercom-target={"menu-AccountSettings-BiometricTab-Entry-TrainingFaceSpaces"}
							floatingLabelText={strings.Training_Face_Spaces}
							value={this.state.defaultFaceSpaceSetCount}
							onChange={this.handleChangeFaceSpaceSetCount}
							errorText={this.state.errorMsg.defaultFaceSpaceSetCount}
							errorStyle={styles.errorTextStyle}
							style={styles.textStyle}
							onBlur={this.handleOnBlurFaceSpace}
						/>
						<div style={{ marginTop: 20 }}>
							<AgreementSet
								finddAgreement={false}
								requireAgreement={this.state.requireAgreement}
								handleRequireAgreement={requireAgreement =>
									patchAccount(this.props.accountId, { AccountSettings: { RequireAgreement: requireAgreement } })
								}
							/>
						</div>
					</SettingsCard>,
					<SettingsCard expanded={"location" === this.state.expanded} cardLabel={strings.Location} handleExpandChange={() => this.onExpandClick("location")} key={4}>
						<div>
							<TextField
								data-intercom-target={"menu-AccountSettings-LocationTab-Entry-DefaultGeofencePerimeter"}
								floatingLabelText={strings.Default_Geofence_Perimeter}
								value={this.state.defaultGeofencePerimeter}
								onChange={this.handleChangeGeofencePerimeter}
								errorText={this.state.errorMsg.defaultGeofencePerimeter}
								errorStyle={styles.errorTextStyle}
								style={styles.textStyle}
								onBlur={this.handleOnBlurGeofencePerimeter}
								type="number"
							/>
							<br />
							<TextField
								data-intercom-target={"menu-AccountSettings-LocationTab-Entry-DefaultMicrolocationPerimeter"}
								floatingLabelText={strings.Default_Microlocation_Perimeter}
								value={this.state.defaultMicroLocationPerimeter}
								onChange={this.handleChangeMicroLocationPerimeter}
								errorText={this.state.errorMsg.defaultMicroLocationPerimeter}
								errorStyle={styles.errorTextStyle}
								sgetUserstyle={styles.textStyle}
								onBlur={this.handleOnBlurMicroLocationPerimeter}
								type="number"
							/>
							<br />
							<Toggle
								data-intercom-target={"menu-AccountSettings-LocationTab-Toggle-EnableLocationException"}
								toggled={this.state.accountEnableLocationException}
								onToggle={this.handleToggleAccountLocationException}
								labelPosition="left"
								label={strings.EnableLocationException}
								style={{ width: 300, marginTop: 25 }}
							/>
							<br />
							<Toggle
								data-intercom-target={"menu-AccountSettings-LocationTab-Toggle-EnableLocationExceptionOnTransfer"}
								toggled={this.state.accountEnableLocationExceptionOnTransfer}
								onToggle={this.handleToggleAccountLocationExceptionOnTransfer}
								labelPosition="left"
								label={strings.Enable_Location_Exception_on_Transfer}
								style={{ width: 300, marginBottom: 15 }}
							/>
						</div>
					</SettingsCard>,
					AccountStore.getEmployeeApproveTimecardEnabled() &&
					<SettingsCard expanded={"time_approvals" === this.state.expanded} cardLabel={strings.Time_Approvals} handleExpandChange={() => this.onExpandClick("time_approvals")} key="time_approvals">
						<TimeApprovals
							accountId={this.props.accountId}
						/>
					</SettingsCard>,
					AccountStore.getTimecardEnabled() &&
					<SettingsCard expanded={"pay" === this.state.expanded} cardLabel={strings.Pay_Configuration} handleExpandChange={() => this.onExpandClick("pay")} key={5}>
						<PayConfiguration />
					</SettingsCard>,
					AccountStore.getHREnabled() &&
					<SettingsCard expanded={"client_settings" === this.state.expanded} cardLabel={strings.Client_Settings} handleExpandChange={() => this.onExpandClick("client_settings")} key="client_settings">
						<ClientSettings />
					</SettingsCard>,
					<SettingsCard expanded={"schedule" === this.state.expanded} cardLabel={strings.Exception_Setting} handleExpandChange={() => this.onExpandClick("schedule")} key="schedule">
						<ExceptionSettings
							enableSchedule={AccountStore.getScheduleEnabled()}
						/>
					</SettingsCard>,
					<SettingsCard expanded={"custom_fields" === this.state.expanded} cardLabel={strings.Custom_Field + "s"} handleExpandChange={() => this.onExpandClick("custom_fields")} key="custom_fields">
						<CustomFieldSettings />
					</SettingsCard>,
					<SettingsCard expanded={"custom_report" === this.state.expanded} cardLabel={strings.Custom_Report} handleExpandChange={() => this.onExpandClick("custom_report")} key="custom_report">
						<CustomReport />
					</SettingsCard>,
					isGlobalAdmin &&
					<SettingsCard expanded={"localization" === this.state.expanded} cardLabel={strings.Localization} handleExpandChange={() => this.onExpandClick("localization")} key={6}>
						<div>
							<SelectField data-intercom-target={"menu-AccountSettings-Localization-Select-Country"} floatingLabelText={strings.DefaultCountry} value={this.state.defaultCountryCode} onChange={this.handleChangeDefaultCountryCode}>
								<MenuItem value={"USA"} primaryText={"United States"} key={"USA"} />
								<MenuItem value={"CAN"} primaryText={"Canada"} key={"CAN"} />
							</SelectField>
							<br />
							<TextField data-intercom-target={"menu-AccountSettings-Localization-Entry-DateFormat"} floatingLabelText={strings.Date_Format} value={this.state.dateFormat} onChange={this.handleChangeDateFormat} onBlur={this.handleOnBlurDateFormat} />
							<br />
							<SelectField data-intercom-target={"menu-AccountSettings-Localization-Select-Currency"} floatingLabelText={strings.Currency} value={this.state.currencyFormat} onChange={this.handleChangeCurrencyFormat}>
								{this.state.currencyList.map((value, i) => (
									<MenuItem value={value} primaryText={value} key={i} />
								))}
							</SelectField>
							<br />
							<TextField data-intercom-target={"menu-AccountSettings-Localization-Entry-LanguageId"} floatingLabelText={strings.Language_Id} value={this.state.languageId} onChange={this.handleChangeLanguageId} onBlur={this.handleOnBlurLanguageId} />
						</div>
					</SettingsCard>,
					isGlobalAdmin &&
					<SettingsCard expanded={"hierarchy" === this.state.expanded} cardLabel={strings.Hierarchy} handleExpandChange={() => this.onExpandClick("hierarchy")} key={7}>
						<div>
							<Toggle
								data-intercom-target={"menu-AccountSettings-Hierarchy-Toggle-RequireExplicitEmployeeHierarchyAssignment"}
								toggled={this.state.employeeAssignedOnly}
								onToggle={this.handleToggleEmployeeAssignedOnly}
								labelPosition="left"
								label={strings.Require_Explicit_Employee_Hierarchy_Assignment}
								style={{ maxWidth: 450, marginBottom: 15 }}
								disabled
							/>
							<Toggle
								data-intercom-target={"menu-AccountSettings-Hierarchy-Toggle-RequireExplicitAccountUserHierarchyAssignment"}
								toggled={this.state.userAssignedOnly}
								onToggle={this.handleToggleUserAssignedOnly}
								labelPosition="left"
								label={strings.Require_Explicit_Account_User_Hierarchy_Assignment}
								style={{ maxWidth: 450 }}
							/>
							<p style={{ width: "50%", marginTop: "20px" }}>{strings.Explain_account_hierarchy_settings}</p>
						</div>
					</SettingsCard>,
					AccountStore.getAccrualManualAdjustmentEnabled() &&
					<SettingsCard expanded={"accrual_adjustment_rules" === this.state.expanded} cardLabel={strings.Accrual_Adjustment_Rules} handleExpandChange={() => this.onExpandClick("accrual_adjustment_rules")} key="accrual_adjustment_rules">
						<AccrualAdjustmentRules
							AccrualSettings={AccountStore.getCurrentAccountSettings().AccrualSettings}
						/>
					</SettingsCard>,
					(isGlobalAdmin || permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ExportFailedPunches)) &&
					<SettingsCard expanded={"integration" === this.state.expanded} cardLabel={strings.Integration} handleExpandChange={() => this.onExpandClick("integration")} key="integration">
						<IntegrationCard />
					</SettingsCard>,
					permissionHelper.allowedImportPermissions().length > 0 &&
					<SettingsCard expanded={"import" === this.state.expanded} cardLabel={strings.ImportCSV} handleExpandChange={() => this.onExpandClick("import")} key="import">
						<ImportCard />
					</SettingsCard>,
					AccountStore.getPayrollEnabled() &&
					(isGlobalAdmin || (
						AuthenticationStore.getLoggedInAccountManagerUser() ?
							permissionHelper.hasAccountManagerPermission(AuthenticationStore.AccountManagerUserPermissions.Clients.AccountScheduledJob) :
							permissionHelper.hasPermission(AuthenticationStore.Permissions.Payroll.ExportPayroll)
					)) &&
					<SettingsCard expanded={"payrollexport" === this.state.expanded} cardLabel={strings.Payroll_Export} handleExpandChange={() => this.onExpandClick("payrollexport")} key="payrollexport">
						<PayrollExportScheduledJob />
					</SettingsCard>,
					(isGlobalAdmin ||
						(AuthenticationStore.getLoggedInAccountManagerUser() ?
							permissionHelper.hasAccountManagerPermission(AuthenticationStore.AccountManagerUserPermissions.Clients.AccountScheduledJob) :
							permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewScheduledJobs)
						)
					) &&
					<SettingsCard expanded={"scheduledjobs" === this.state.expanded} cardLabel={strings.ScheduledJobs} handleExpandChange={() => this.onExpandClick("scheduledjobs")} key="scheduledjobs">
						<ScheduledJobsCard />
					</SettingsCard>,
					isGlobalAdmin &&
					<SettingsCard expanded={"recalc_timecards" === this.state.expanded} cardLabel={strings.Recalculate_Timecards} handleExpandChange={() => this.onExpandClick("recalc_timecards")} key="recalc_timecards">
						<RecalcTimecards />
					</SettingsCard>,
					isGlobalAdmin && AccountStore.getHREnabled() &&
					<SettingsCard expanded={"notetypes" === this.state.expanded} cardLabel={strings.NoteTypes} handleExpandChange={() => this.onExpandClick("notetypes")} key="notetypes">
						<NoteTypesCard />
					</SettingsCard>,
					(isGlobalAdmin ||
						permissionHelper.hasAccountManagerPermission(AuthenticationStore.AccountManagerUserPermissions.Clients.AccountFeatureSetting)
					) &&
					[
						<SettingsCard expanded={"features" === this.state.expanded} cardLabel={strings.Features} handleExpandChange={() => this.onExpandClick("features")} key={"feature"}>
							<AccountFeatureSettings
								account={AccountStore.getCurrentAccount()}
							/>
						</SettingsCard>,
						<SettingsCard expanded={"mobile" === this.state.expanded} cardLabel={strings.Mobile} handleExpandChange={() => this.onExpandClick("mobile")} key={"mobile"}>
							<AccountMobileFeatureSettings
								account={AccountStore.getCurrentAccount()}
							/>
						</SettingsCard>
					]
				]}
			</div>
		)
	}
}
